import React, { useEffect, useRef } from 'react';
import theme from '@shared/utils/themeColors';

interface SupportMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaUrl: string;
  mediaType: 'image' | 'video' | 'gif';
}

/**
 * Modal for previewing media (images, GIFs, videos) in the support chat
 */
const SupportMediaModal: React.FC<SupportMediaModalProps> = ({
  isOpen,
  onClose,
  mediaUrl,
  mediaType
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Close on click outside
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef} 
        className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="flex items-center justify-center p-4 max-h-[80vh]">
          {mediaType === 'image' || mediaType === 'gif' ? (
            <img 
              src={mediaUrl} 
              alt="Media preview" 
              className="max-w-full max-h-[70vh] object-contain"
            />
          ) : mediaType === 'video' ? (
            <video 
              src={mediaUrl} 
              controls 
              className="max-w-full max-h-[70vh]"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SupportMediaModal;
